import * as React from "react";
import { connect } from "react-redux";
import { navigate } from "~/router";
import { useLocationProducts } from "~/apps/pos/apis/products";
import { useEffect, useMemo, useState } from "react";
import { Features, UserRole } from "~/apps/shared/consts";
import { useRoute } from "react-router5";
import { getProductFilter, getProductParams } from "~/apps/pos/shared/services/qsfilter";
import ProductsSection from "../../components/products/ProductsSection";
import ReactSelect from "react-select";
import Loader from "~/common/components/Loader";
import { createOrgLocationsHelper } from "~/apps/shared/services/forms";
import { useProductDeleteConfirm } from "../../components/products/useProductDeleteConfirm";
import { getGroupedProducts } from "~/apps/pos/helpers";


const allProductsRouteName = "pos.admin.products.all-products";

const ProductList = ({ features, currentUser}) => {
  const {
    route: { params, name: routeName },
  } = useRoute();

  const filter = getProductFilter(params);
  const isAgent = currentUser.role === UserRole.AGENT;
  const isWerehouseManager = currentUser.role === UserRole.WAREHOUSE_MANAGER;
  const isCategoryEnabled = features?.includes(Features.TRANSACTION_DATA_EXPOSED);

  const [, forceUpdate] = useState({});
  const locationHelper = useMemo(() => createOrgLocationsHelper(() => forceUpdate({}), currentUser.organizationId), []);
  const { deleteConfirm, showDeleteConfirm } = useProductDeleteConfirm();

  useEffect(() => {
    if (filter.locationId || routeName !== allProductsRouteName) return;

    const locationId = isWerehouseManager
      ? currentUser.locationId
      : isAgent && locationHelper.options?.length
      ? locationHelper.options[0].value
      : undefined;

    if (locationId) {
      navigate(allProductsRouteName, getProductParams({ locationId }));
    }
  }, [currentUser.locationId, locationHelper.options?.length, filter.locationId]);

  const { data: products, isFetching: isProductsLoading } = useLocationProducts(filter.locationId as string);

  const { sharedProductGroups, specificProductGroups } = useMemo(() => {
    if (!products) return { sharedProductGroups: [], specificProductGroups: [] };

    const sharedProductsData = products.filter((product) => !product.locationId);
    const specificProductsData = products.filter((product) => product.locationId);
    const sharedProductGroups = getGroupedProducts(sharedProductsData);
    const specificProductGroups = getGroupedProducts(specificProductsData);

    return { sharedProductGroups, specificProductGroups };
  }, [products]);

  const handleLocationChange = (option) => {
    filter.locationId = option.value;
    navigate("pos.admin.products", getProductParams(filter));
  };

  const handleEdit = (product) => {
    navigate("pos.admin.products.all-products.edit", { ...getProductParams(filter), productId: product.id });
  };

  const isLoading = isProductsLoading || !!locationHelper.isLoading;

  return (
    <>
      {isLoading && <Loader className="load--ol" />}

      <ProductsSection
        title="Shared products"
        productGroups={sharedProductGroups}
        isCategoryEnabled={isCategoryEnabled}
        canManage={isAgent}
        onEdit={handleEdit}
        onDelete={showDeleteConfirm}
      />

      {isAgent && !!locationHelper.options?.length && (
        <div style={{ display: "flex", justifyContent: "end", alignItems: "center",  cursor: "pointer", marginTop: "25px", gap: "6px" }}>
          <div style={{fontSize: "18px", fontWeight: "bold"}}>{"Locations:"}</div>
          <ReactSelect
            name="locationId"
            label="Location"
            className="filter--drop"
            placeholder="All Locations"
            classNamePrefix="filter--drop"
            value={locationHelper.getSelectValue(params.locationId)}
            options={locationHelper.options}
            onChange={handleLocationChange}
            onInputChange={locationHelper.search}
            isLoading={!!locationHelper.isLoading}
          />
        </div>
      )}

      <ProductsSection
        title="Local products"
        productGroups={specificProductGroups}
        onEdit={handleEdit}
        canManage={isAgent || isWerehouseManager}
        isCategoryEnabled={isCategoryEnabled}
        onDelete={showDeleteConfirm}
      />
      {deleteConfirm}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    currentUser: state.shared.auth.currentUser,
    features: state.shared.information.entities.features,
  };
};

export default connect(mapStateToProps)(ProductList);
